'use client'

import { useEffect } from 'react'

type Target = 'head' | 'body-top' | 'body-bottom'

/**
 * Recria e EXECUTA os <script> de um HTML bruto na posição alvo.
 *
 * Por quê: innerHTML/dangerouslySetInnerHTML NÃO executam <script> (spec HTML). O markup
 * estático (meta/link/style/noscript) é renderizado no SSR (ver serverHtml.tsx); aqui só
 * tratamos os <script>, recriando cada um como elemento novo (clonar atributos + conteúdo)
 * — ao anexar ao documento, o browser executa (GTM, GA/gtag, AdSense, pixels, chat).
 *
 * Conteúdo vem do global headFooterCode → editável APENAS por Admin (fronteira RBAC).
 */
export function RawScripts({ html, target }: { html: string; target: Target }) {
  useEffect(() => {
    const code = html?.trim()
    if (!code) return

    const root: HTMLElement = target === 'head' ? document.head : document.body

    const tpl = document.createElement('template')
    tpl.innerHTML = code // template.content é inerte: nada executa/baixa ainda

    const inserted: HTMLScriptElement[] = []
    const before = target === 'body-top' ? root.firstChild : null

    for (const old of Array.from(tpl.content.querySelectorAll('script'))) {
      const s = document.createElement('script')
      for (const { name, value } of Array.from(old.attributes)) s.setAttribute(name, value)
      s.text = old.text
      if (before) root.insertBefore(s, before) // preserva ordem antes do conteúdo existente
      else root.appendChild(s)
      inserted.push(s)
    }

    return () => {
      for (const s of inserted) s.parentNode?.removeChild(s)
    }
  }, [html, target])

  return null
}
